import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getAllPipelineStages,
  createPipelineStage,
  updatePipelineStage,
  deletePipelineStage,
  type PipelineStage,
  type CreatePipelineStagePayload,
  type UpdatePipelineStagePayload,
} from '../../services/RecruiterServices/PipelineStageServices/pipelineStageService';

interface PipelineStageState {
  stages: PipelineStage[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

const initialState: PipelineStageState = {
  stages: [],
  loading: false,
  submitting: false,
  error: null,
};

export const fetchPipelineStages = createAsyncThunk(
  'pipelineStages/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllPipelineStages();
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to fetch pipeline stages');
    }
  },
);

export const addPipelineStage = createAsyncThunk(
  'pipelineStages/add',
  async (payload: CreatePipelineStagePayload, { rejectWithValue }) => {
    try {
      return await createPipelineStage(payload);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to create pipeline stage');
    }
  },
);

export const editPipelineStage = createAsyncThunk(
  'pipelineStages/edit',
  async ({ id, payload }: { id: number; payload: UpdatePipelineStagePayload }, { rejectWithValue }) => {
    try {
      return await updatePipelineStage(id, payload);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to update pipeline stage');
    }
  },
);

export const removePipelineStage = createAsyncThunk(
  'pipelineStages/remove',
  async (id: number, { rejectWithValue }) => {
    try {
      await deletePipelineStage(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to delete pipeline stage');
    }
  },
);

const pipelineStageSlice = createSlice({
  name: 'pipelineStages',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchAll
      .addCase(fetchPipelineStages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPipelineStages.fulfilled, (state, action) => {
        state.loading = false;
        state.stages = action.payload;
      })
      .addCase(fetchPipelineStages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // add
      .addCase(addPipelineStage.pending, (state) => { state.submitting = true; })
      .addCase(addPipelineStage.fulfilled, (state, action) => {
        state.submitting = false;
        state.stages.push(action.payload);
      })
      .addCase(addPipelineStage.rejected, (state) => { state.submitting = false; })

      // edit
      .addCase(editPipelineStage.pending, (state) => { state.submitting = true; })
      .addCase(editPipelineStage.fulfilled, (state, action) => {
        state.submitting = false;
        const idx = state.stages.findIndex((s) => s.stage_id === action.payload.stage_id);
        if (idx !== -1) state.stages[idx] = action.payload;
      })
      .addCase(editPipelineStage.rejected, (state) => { state.submitting = false; })

      // remove
      .addCase(removePipelineStage.pending, (state) => { state.submitting = true; })
      .addCase(removePipelineStage.fulfilled, (state, action) => {
        state.submitting = false;
        state.stages = state.stages.filter((s) => s.stage_id !== action.payload);
      })
      .addCase(removePipelineStage.rejected, (state) => { state.submitting = false; });
  },
});

export default pipelineStageSlice.reducer;
